import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useHtClient } from '../providers/ht';
import type { AxiosError } from 'axios';

// Types
interface TokenResponse {
  access_token: string;
  token_type: string;
  instance_url: string;
  id: string;
  issued_at: string;
}

export interface Account {
  Id: string;
  Name: string;
}

interface Contact {
  Id: string;
  FirstName: string;
  LastName: string;
  Email: string;
  Phone: string;
  Title?: string;
}

interface AccountsResponse {
  totalSize: number;
  done: boolean;
  records: Account[];
  nextRecordsUrl?: string;
}

interface ContactsResponse {
  totalSize: number;
  records: Contact[];
}

// Hook to refresh Salesforce OAuth token
export const useRefreshSalesforceToken = () => {
  const { data: client } = useHtClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async () => {
      console.log('🔄 SALESFORCE: Refreshing token');
      
      if (!client)
        throw new Error("API client not initialized");
      
      const res = await client.post("/salesforce/refresh-token");
      
      console.log('✅ SALESFORCE: Token refreshed successfully');
      
      // Invalidate any Salesforce-related queries to refetch with new token
      queryClient.invalidateQueries({ queryKey: ["salesforceAccounts"] });
      queryClient.invalidateQueries({ queryKey: ["salesforceAccount"] });
      queryClient.invalidateQueries({ queryKey: ["salesforceContacts"] });
      queryClient.invalidateQueries({ queryKey: ["salesforceContact"] });
      
      return res.data as TokenResponse;
    }
  });
};

// Hook to fetch all Salesforce accounts
export const useAccounts = () => {
  const { data: client, isLoading: isClientLoading } = useHtClient();
  const { mutateAsync: refreshToken } = useRefreshSalesforceToken();
  
  return useQuery({
    queryKey: ["salesforceAccounts"],
    queryFn: async () => {
      console.log('📊 SALESFORCE: Fetching accounts');
      
      if (!client) {
        console.log('❌ SALESFORCE: Client not initialized');
        throw new Error("API client not initialized");
      }
      
      try {
        const res = await client.get("/salesforce/accounts");
        console.log('✅ SALESFORCE: Successfully fetched accounts');
        return res.data as AccountsResponse;
      } catch (error: any) {
        console.log('❌ SALESFORCE: Error fetching accounts', error);
        
        // Check if error is due to expired token
        if (error.response?.status === 401) {
          console.log('🔑 SALESFORCE: Token expired, refreshing...');
          await refreshToken();
          // Retry the request with new token
          const res = await client.get("/salesforce/accounts");
          console.log('✅ SALESFORCE: Successfully fetched accounts after token refresh');
          return res.data as AccountsResponse;
        }
        throw error;
      }
    },
    enabled: !isClientLoading && !!client,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

// Hook to fetch a specific Salesforce account by ID
export const useAccount = (accountId: string) => {
  const { data: client, isLoading: isClientLoading } = useHtClient();
  const { mutateAsync: refreshToken } = useRefreshSalesforceToken();
  
  console.log('🔄 SALESFORCE HOOK: Initializing with:', {
    hasClient: !!client,
    isClientLoading,
    accountId
  });
  
  return useQuery({
    queryKey: ["salesforceAccount", accountId],
    queryFn: async () => {
      console.log(`📊 SALESFORCE: Fetching account ${accountId}`);
      console.log('🔍 SALESFORCE: Client state:', {
        hasClient: !!client,
        isClientLoading
      });
      
      if (!client) {
        console.log('❌ SALESFORCE: Client not initialized, throwing error');
        throw new Error("API client not initialized");
      }
      
      if (!accountId) {
        console.log('❌ SALESFORCE: No account ID provided, returning null');
        return null;
      }
      
      try {
        console.log('🚀 SALESFORCE: Making request to:', `/salesforce/accounts/${accountId}`);
        const res = await client.get(`/salesforce/accounts/${accountId}`);
        console.log('✅ SALESFORCE: Got response:', res.data);
        return res.data;
      } catch (error: any) {
        console.log('❌ SALESFORCE: Request failed:', error);
        if (error.response?.status === 401) {
          console.log('🔄 SALESFORCE: Token expired, refreshing...');
          await refreshToken();
          const res = await client.get(`/salesforce/accounts/${accountId}`);
          console.log('✅ SALESFORCE: Got response after refresh:', res.data);
          return res.data;
        }
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: !!accountId && !!client && !isClientLoading
  });
};

// Hook to fetch contacts for a specific account
export const useAccountContacts = (accountId: string) => {
  const { data: client } = useHtClient();
  const { mutateAsync: refreshToken } = useRefreshSalesforceToken();
  
  return useQuery({
    queryKey: ["salesforceContacts", accountId],
    queryFn: async () => {
      console.log(`📊 SALESFORCE: Fetching contacts for account ${accountId}`);
      
      if (!client)
        throw new Error("API client not initialized");
      
      if (!accountId)
        return null;
      
      try {
        const res = await client.get(`/salesforce/accounts/${accountId}/contacts`);
        return res.data;
      } catch (error: any) {
        if (error.response?.status === 401) {
          await refreshToken();
          const res = await client.get(`/salesforce/accounts/${accountId}/contacts`);
          return res.data;
        }
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: !!accountId
  });
};

// Hook to fetch a specific contact by ID
export const useContact = (contactId: string) => {
  const { data: client } = useHtClient();
  const { mutateAsync: refreshToken } = useRefreshSalesforceToken();
  
  return useQuery({
    queryKey: ["salesforceContact", contactId],
    queryFn: async () => {
      console.log(`📊 SALESFORCE: Fetching contact ${contactId}`);
      
      if (!client)
        throw new Error("API client not initialized");
      
      if (!contactId)
        return null;
      
      try {
        const res = await client.get(`/salesforce/contacts/${contactId}`);
        return res.data;
      } catch (error: any) {
        if (error.response?.status === 401) {
          await refreshToken();
          const res = await client.get(`/salesforce/contacts/${contactId}`);
          return res.data;
        }
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: !!contactId
  });
};

// Hook to search Salesforce accounts by name
export const useAccountSearch = (searchTerm: string) => {
  const { data: client } = useHtClient();
  const { mutateAsync: refreshToken } = useRefreshSalesforceToken();
  
  return useQuery({
    queryKey: ["salesforceAccountSearch", searchTerm],
    queryFn: async () => {
      console.log(`🔍 SALESFORCE: Searching accounts with term "${searchTerm}"`);
      
      if (!client)
        throw new Error("API client not initialized");
      
      if (!searchTerm || searchTerm.trim().length < 2)
        return { records: [] };
      
      try {
        // Use the existing accounts endpoint and filter client-side
        // In a full implementation, you might want to add a search endpoint to your API
        const res = await client.get(`/salesforce/accounts`);
        const data = res.data as AccountsResponse;
        
        // Filter accounts by name containing the search term
        const filteredRecords = data.records.filter(account => 
          account.Name.toLowerCase().includes(searchTerm.toLowerCase())
        );
        
        return {
          totalSize: filteredRecords.length,
          records: filteredRecords
        };
      } catch (error: any) {
        if (error.response?.status === 401) {
          await refreshToken();
          const res = await client.get(`/salesforce/accounts`);
          const data = res.data as AccountsResponse;
          
          const filteredRecords = data.records.filter(account => 
            account.Name.toLowerCase().includes(searchTerm.toLowerCase())
          );
          
          return {
            totalSize: filteredRecords.length,
            records: filteredRecords
          };
        }
        throw error;
      }
    },
    staleTime: 60 * 1000, // 1 minute
    refetchOnWindowFocus: false,
    enabled: searchTerm.trim().length >= 2
  });
};

// Hook to get contacts for an account (with titles)
export const useAccountContactsWithTitles = (accountId: string) => {
  const { data: client } = useHtClient();
  const { mutateAsync: refreshToken } = useRefreshSalesforceToken();

  return useQuery({
    queryKey: ['salesforceAccountContactsWithTitles', accountId],
    queryFn: async () => {
      console.log(`📊 SALESFORCE: Fetching contacts with titles for account ${accountId}`);
      
      if (!client) throw new Error("API client not initialized");
      if (!accountId) return null;
      
      try {
        const res = await client.get(`/salesforce/accounts/${accountId}/contacts-with-titles`);
        return res.data;
      } catch (error: any) {
        if (error.response?.status === 401) {
          await refreshToken();
          const res = await client.get(`/salesforce/accounts/${accountId}/contacts-with-titles`);
          return res.data;
        }
        throw error;
      }
    },
    enabled: !!accountId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
    refetchOnWindowFocus: false,
  });
};

// Hook to search for Salesforce account by email
export const useSearchAccountByEmail = () => {
  const { data: client } = useHtClient();
  const { mutateAsync: refreshToken } = useRefreshSalesforceToken();
  
  return useMutation({
    mutationFn: async (email: string) => {
      console.log(`🔍 SALESFORCE: Searching account by email: ${email}`);
      
      if (!client) throw new Error("API client not initialized");
      if (!email) throw new Error("Email is required");
      
      try {
        const res = await client.get(`/salesforce/accounts/search/email/${encodeURIComponent(email)}`);
        return res.data;
      } catch (error: any) {
        if (error.response?.status === 401) {
          await refreshToken();
          const res = await client.get(`/salesforce/accounts/search/email/${encodeURIComponent(email)}`);
          return res.data;
        }
        throw error;
      }
    }
  });
};

export const useEmailBySfAccount = (sfAccountId: string | undefined) => {
  const { data: client } = useHtClient();
  return useQuery({
    queryKey: ['emailBySfAccount', sfAccountId],
    queryFn: async () => {
      if (!client || !sfAccountId) throw new Error('Missing client or sfAccountId');
      const response = await client.get(`/salesforce/accounts/email-by-id/${sfAccountId}`);
      return response.data.email;
    },
    enabled: !!client && !!sfAccountId,
    staleTime: 5 * 60 * 1000,
  });
}; 
